import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Phone";
import { motion } from "framer-motion";
import { productsData } from "../services/apiService";
import { SEO } from "../components/SEO";
import {
  AnimatedInView,
  Section,
  GlassCard,
  SpecsTable,
  ProductCard,
  PageBreadcrumbs,
  CTABand,
  staggerChild,
} from "../components/ui";
import NotFound from "./NotFound";
import {
  generateProductStructuredData,
  generateBreadcrumbStructuredData,
} from "../utils/structuredData";
import { REACT_APP_SITE_URL } from "../config/seo";

const PLACEHOLDER_IMAGE = "/images/products/placeholder.png";
const PHONE = "+919013573235";

const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  // Static catalog data — resolved synchronously for prerender/hydration parity
  const product = useMemo(
    () => productsData.find((p) => p.slug === slug) ?? null,
    [slug],
  );
  const related = useMemo(() => {
    if (!product) return [];
    const sameCategory = productsData.filter(
      (p) => p.category === product.category && p.id !== product.id,
    );
    const others = productsData.filter(
      (p) => p.category !== product.category && p.id !== product.id,
    );
    return [...sameCategory, ...others].slice(0, 4);
  }, [product]);

  // Unknown slug → 404 (SEO component inside NotFound sets noindex)
  if (product === null) return <NotFound />;

  const pageUrl = `${REACT_APP_SITE_URL}/products/${product.slug}`;
  const whatsappHref = `https://wa.me/${PHONE.replace("+", "")}?text=${encodeURIComponent(
    `Hi, I'm interested in the ${product.name}. Please share price and availability.`,
  )}`;
  const topFeatures = (product.features || []).slice(0, 4);
  const hasSpecs = product.specs && Object.keys(product.specs).length > 0;
  const hasFeatures = (product.features || []).length > 0;

  return (
    <>
      <SEO
        title={product.name}
        description={product.description}
        image={product.image}
        jsonLd={[
          generateProductStructuredData(product, pageUrl, REACT_APP_SITE_URL),
          generateBreadcrumbStructuredData([
            { name: "Home", item: REACT_APP_SITE_URL },
            { name: "Products", item: `${REACT_APP_SITE_URL}/products` },
            { name: product.name, item: pageUrl },
          ]),
        ]}
      />

      <Container maxWidth="lg" sx={{ pt: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <PageBreadcrumbs
          items={[
            { name: "Home", path: "/" },
            { name: "Products", path: "/products" },
            { name: product.name },
          ]}
        />

        {/* Top: image + summary */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
            gap: { xs: 4, md: 6 },
            alignItems: "start",
            mb: { xs: 5, md: 7 },
          }}
        >
          <AnimatedInView variant="fade">
            <GlassCard
              tone="light"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#FFFFFF",
                minHeight: { xs: 280, md: 400 },
              }}
            >
              <Box
                component="img"
                src={product.image}
                alt={product.name}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.src = PLACEHOLDER_IMAGE;
                }}
                sx={{ maxWidth: "100%", maxHeight: 380, objectFit: "contain" }}
              />
            </GlassCard>
          </AnimatedInView>

          <AnimatedInView>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={product.category}
                sx={{ bgcolor: "rgba(199,93,44,0.1)", color: "secondary.dark" }}
              />
              {product.isNew && <Chip label="New" color="secondary" />}
            </Stack>
            <Typography variant="h1" component="h1" sx={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", mb: 2 }}>
              {product.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {product.description}
            </Typography>

            {topFeatures.length > 0 && (
              <List dense sx={{ mb: 3 }}>
                {topFeatures.map((feature) => (
                  <ListItem key={feature} disableGutters>
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <CheckCircleIcon sx={{ color: "secondary.main", fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary={feature} primaryTypographyProps={{ variant: "body2", fontWeight: 500 }} />
                  </ListItem>
                ))}
              </List>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                startIcon={<WhatsAppIcon />}
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: "#25D366",
                  px: 4,
                  "&:hover": { bgcolor: "#1EBE5A" },
                }}
              >
                Enquire on WhatsApp
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<PhoneIcon />}
                href={`tel:${PHONE}`}
                sx={{ px: 4 }}
              >
                Call Us
              </Button>
            </Stack>
          </AnimatedInView>
        </Box>
      </Container>

      {/* Specs */}
      {hasSpecs && (
        <Section bg="paper" py={{ xs: 5, md: 7 }} id="specifications">
          <AnimatedInView>
            <Typography variant="h2" component="h2" sx={{ mb: 3 }}>
              Specifications
            </Typography>
            <SpecsTable specs={product.specs!} />
          </AnimatedInView>
        </Section>
      )}

      {/* Full features */}
      {hasFeatures && (
        <Section py={{ xs: 5, md: 7 }} id="features">
          <AnimatedInView>
            <Typography variant="h2" component="h2" sx={{ mb: 3 }}>
              Features
            </Typography>
            <Box
              component="ul"
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: 1.5,
                listStyle: "none",
                p: 0,
                m: 0,
              }}
            >
              {product.features!.map((feature) => (
                <Box component="li" key={feature} sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}>
                  <CheckCircleIcon sx={{ color: "secondary.main", fontSize: 20, mt: 0.4 }} />
                  <Typography variant="body1">{feature}</Typography>
                </Box>
              ))}
            </Box>
          </AnimatedInView>
        </Section>
      )}

      {/* Related products */}
      {related.length > 0 && (
        <Section bg="paper" py={{ xs: 5, md: 7 }} id="related">
          <AnimatedInView>
            <Typography variant="h2" component="h2" sx={{ mb: 4 }}>
              Related products
            </Typography>
          </AnimatedInView>
          <AnimatedInView variant="stagger">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 3,
              }}
            >
              {related.map((rel) => (
                <motion.div key={rel.id} variants={staggerChild} style={{ height: "100%" }}>
                  <ProductCard product={rel} />
                </motion.div>
              ))}
            </Box>
          </AnimatedInView>
        </Section>
      )}

      <CTABand
        title={`Interested in the ${product.name}?`}
        subtitle="Get today's best price, availability and a live demo — free consultation for your business."
        whatsappMessage={`Hi, I'm interested in the ${product.name}. Please share price and availability.`}
      />
    </>
  );
};

export default ProductDetail;
